/**
 * Cookie-JWT session state against the CC server (`/api/v1/auth/*`).
 *
 * The whole UI sits behind this guard (`RootGate` in router.tsx). Besides
 * "authenticated or not" it exposes the first-login state machine
 * (`complete_setup` → `enroll_mfa` → `mfa` → `ready`) and the caller's role
 * and bot grants so pages can hide admin-only controls. Authorization is
 * still enforced server-side; the UI only mirrors it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fleetApi, type AuthStep, type MeResponse, type Operator } from "../api/fleet";

export function useAuth(): {
  operator: Operator | undefined;
  me: MeResponse | undefined;
  step: AuthStep | undefined;
  isAdmin: boolean;
  grants: string[] | "all";
  isAuthenticated: boolean;
  isReady: boolean;
  isLoading: boolean;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => fleetApi.auth.me(),
    retry: false,
    staleTime: 60_000,
  });

  const authenticated = !!data && !error;
  return {
    operator: data?.operator,
    me: data,
    step: data?.next,
    isAdmin: data?.user.role === "admin",
    grants: data?.grants ?? [],
    isAuthenticated: authenticated,
    isReady: authenticated && data?.next === "ready",
    isLoading,
  };
}

function invalidateMe(qc: ReturnType<typeof useQueryClient>) {
  return () => void qc.invalidateQueries({ queryKey: ["auth", "me"] });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      fleetApi.auth.login(email, password),
    onSuccess: invalidateMe(qc),
  });
}

export function useCompleteSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { new_password: string; display_name: string }) => fleetApi.auth.setup(body),
    onSuccess: invalidateMe(qc),
  });
}

export function useMfaEnroll() {
  return useMutation({ mutationFn: () => fleetApi.auth.mfaEnroll() });
}

export function useMfaActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => fleetApi.auth.mfaActivate(code),
    onSuccess: invalidateMe(qc),
  });
}

export function useMfaVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => fleetApi.auth.mfaVerify(code),
    onSuccess: invalidateMe(qc),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fleetApi.auth.logout(),
    onSettled: () => {
      qc.clear();
      void qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}
